import React, { useContext, useEffect, useState } from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import {
  Edit, CheckCircle, Category, AccountBalance, NoteAdd,
  Person, Flag, RocketLaunch,
} from "@mui/icons-material";
import { ProjectContext } from "../../../AppContext/ProjectContext";
import { getProjectActivityInRealTime } from "../../../Config/db/project-activity.operations";
import moment from "moment/moment";

const EVENT_CONFIG = {
  status_change: { icon: Flag, color: "#1976d2", label: "Status" },
  classified:    { icon: Category, color: "#6a1b9a", label: "Classified" },
  work_log:      { icon: NoteAdd, color: "#2e7d32", label: "Work Log" },
  disbursement:  { icon: AccountBalance, color: "#e65100", label: "Disbursement" },
  created:       { icon: RocketLaunch, color: "#1b5e20", label: "Created" },
  updated:       { icon: Edit, color: "#455a64", label: "Updated" },
  default:       { icon: CheckCircle, color: "#757575", label: "Event" },
};

export default function ActivityTimeline() {
  const [events, setEvents] = useState([]);
  const { projectInfo } = useContext(ProjectContext);

  useEffect(() => {
    if (projectInfo?.id) {
      const unsub = getProjectActivityInRealTime(projectInfo.id, setEvents);
      return () => unsub?.();
    }
  }, [projectInfo?.id]);

  if (events.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 4, textAlign: "center", border: "1px solid #e0e0e0", borderRadius: 2 }}
      >
        <RocketLaunch sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
        <Typography color="text.secondary">
          No activity recorded yet. Events will appear here as the project progresses.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {events.map((event, idx) => {
        const config = EVENT_CONFIG[event.type] || EVENT_CONFIG.default;
        const Icon = config.icon;
        const ts = event.timestamp?.seconds
          ? moment.unix(event.timestamp.seconds).format("MMM D, YYYY h:mm A")
          : "";

        return (
          <Box
            key={event.id || idx}
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              position: "relative",
              "&:not(:last-child)::before": {
                content: '""',
                position: "absolute",
                left: 15,
                top: 36,
                bottom: -16,
                width: 2,
                bgcolor: "#e0e0e0",
              },
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                bgcolor: `${config.color}18`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Icon sx={{ fontSize: 16, color: config.color }} />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={config.label}
                  size="small"
                  sx={{
                    bgcolor: `${config.color}18`,
                    color: config.color,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: 20,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {ts}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {event.description}
              </Typography>
              {event.userName && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                  <Person sx={{ fontSize: 12, color: "text.disabled" }} />
                  <Typography variant="caption" color="text.secondary">
                    {event.userName}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
